<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';
  import { Button, ButtonDropdown, DropdownItem, DropdownMenu, DropdownToggle } from '@sveltestrap/sveltestrap';

  import { EmptyState } from '@client/components/empty-state';
  import { Loader } from '@client/components/loader';
  import { getNotificationsContext } from '@client/components/notifications';
  import { PageHeader } from '@client/components/page';

  import {
    addRoute,
    backgroundOptions,
    cancelReorder,
    deleteRoute,
    disableManualMove,
    dropIndex,
    groupOptions,
    hasRouting,
    initialized,
    isSaving,
    loadBackgrounds,
    loading,
    loadRoutes,
    priority,
    reordering,
    saveReorder,
    selectedRoute,
    senderGroups,
    startIndex,
    startPosition,
    startReordering,
    targetPosition,
    updatePositions,
    updatePriorityPositions
  } from '@client/stores/virtual-background-routing';

  import { DiagnosticsModal, EditModal } from './modals';
  import { Route } from './route';

  const { addNotification } = getNotificationsContext();

  // UI-only state
  let showDiagnosticModal = false;

  const handleCancelReorder = () => {
    cancelReorder();
  };

  const handleSaveReorder = async () => {
    const result = await saveReorder();

    if (!result.success) {
      addNotification({
        text: $format('label.FAILURE.UPDATE_ROUTING_PRIORITY'),
        type: 'error',
        autohide: 2500,
        position: 'top-right'
      });
    } else {
      addNotification({
        text: $format('label.SUCCESS.UPDATE_ROUTING_PRIORITY'),
        type: 'success',
        autohide: 2500,
        position: 'top-right'
      });
    }
  };

  const handleDelete = async () => {
    const result = await deleteRoute();

    if (!result.success) {
      addNotification({
        text: $format('label.FAILURE.DELETE_ROUTING_PRIORITY'),
        type: 'error',
        autohide: 2500,
        position: 'top-right'
      });
    }
  };

  const handleAddRoute = () => {
    addRoute();
  };

  onMount(async () => {
    await Promise.all([loadRoutes(), loadBackgrounds()]);
  });
</script>

<div id="routes" class="page">
  <Loader backdrop show={$loading} />

  <PageHeader
    title={$format('label.ASSIGNMENT')}
    paths={[[$format('label.EMPLOYEE_BRANDING'), '/brandkit'], [$format('label.ASSIGNMENT')]]}
    class="shadow-none"
  >
    {#if $initialized && !$reordering}
      <ButtonDropdown class="routing-actions hide--mobile me-2">
        <DropdownToggle color="secondary" caret>{$format('label.RESOURCES')}</DropdownToggle>
        <DropdownMenu>
          <DropdownItem on:click={() => (showDiagnosticModal = true)}>
            {$format('label.ROUTING_BACKGROUND_DIAGNOSTICS')}
          </DropdownItem>
        </DropdownMenu>
      </ButtonDropdown>

      <Button color="primary" on:click={handleAddRoute}>{$format('label.ADD_ASSIGNMENT')}</Button>
    {/if}
  </PageHeader>

  <div class="page-content">
    <div class="routes-layout">
      {#if $hasRouting}
        <div class="priority">
          <div class="priority-list">
            {#if $reordering}
              <div class="manual-move">
                <span>{$format('label.MOVE')}</span>
                <input type="number" bind:value={$startPosition} min="1" max={$priority.length} />
                <span>{$format('label.MOVE_TO')}</span>
                <span class="hide--mobile">{$format('label.POSITION').toLowerCase()}</span>
                <input type="number" bind:value={$targetPosition} min="1" max={$priority.length} />
                <Button size="sm" color="secondary" on:click={updatePositions} disabled={$disableManualMove}>
                  {$format('label.UPDATE')}
                </Button>
              </div>
            {/if}
            {#if $priority}
              {#each $priority as route, index}
                <Route
                  bind:reordering={$reordering}
                  bind:priority={$priority}
                  bind:dropIndex={$dropIndex}
                  bind:startIndex={$startIndex}
                  bind:senderGroups={$senderGroups}
                  bind:selectedRoute={$selectedRoute}
                  routeIndex={index}
                  onRefresh={loadRoutes}
                  onDelete={handleDelete}
                  {route}
                  {updatePriorityPositions}
                />
              {/each}
            {/if}
          </div>
        </div>
      {:else if $initialized}
        <EmptyState
          icon="git-pull-request"
          heading={$format('label.EMPTY_STATE.BACKGROUND_ROUTING_HEADING')}
          description={$format('label.EMPTY_STATE.BACKGROUND_ROUTING_DESCRIPTION')}
          action={$format('label.EMPTY_STATE.BACKGROUND_ROUTING_ACTION')}
          on:click={handleAddRoute}
        />
      {/if}
    </div>
  </div>
</div>

<DiagnosticsModal bind:show={showDiagnosticModal} />

{#if $selectedRoute}
  <EditModal
    bind:priority={$priority}
    bind:senderGroups={$senderGroups}
    show={$selectedRoute}
    bind:route={$selectedRoute}
    onAdd={handleSaveReorder}
    {updatePriorityPositions}
    groupOptions={$groupOptions}
    backgroundOptions={$backgroundOptions}
  />
{/if}

<style lang="scss" src="./routing.scss"></style>
